import classNames from 'classnames';
import { differenceInSeconds, isAfter, parseISO } from 'date-fns';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { useInterval } from '../../hooks/interval';
import { FoodTray, FoodType } from '../../models';
import { userSelectors } from '../../redux';
import { getFoodTypeName } from '../../redux/foodtypes/selectors';
import { AudioService } from '../../services/AudioService';
import { WarningTimer } from '..';

const ding = require('../../assets/sounds/ding.mp3');

interface Props {
  item: FoodTray;
}

enum Color {
  GRAY = '#929292',
  TIMER = '#FFFFFF',
  WARNING = '#EBB14F',
  WARNING2 = '#FF4F48',
  OVERTIME = '#FF4F48',
}

const TRANSPARENCY_HEX = '80'; // 50% in hex

const Gastronom: FC<Props> = ({ item }) => {
  const userInfo = useSelector(userSelectors.getUserInfo);
  const warningTimerSetting = useSelector(userSelectors.getWarningTime);
  const [timeOnTimer, setTimeOnTimer] = useState<number>(null);
  const [delay, setDelay] = useState(null);
  const [type, setType] = useState<FoodType | null>(null);
  const cell = useRef(null);

  const playWarning = async () => {
    await AudioService.playSound();
  };

  useEffect(() => {
    //If we only check on warningTimerSetting, and it is set to 0, then js returns falsy
    if (warningTimerSetting >= 0) {
      setType(item?.foodItem?.overrideFoodType || item?.defaultFoodType);
    }
  }, [item, warningTimerSetting]);

  useEffect(() => {
    const timer = item?.foodItem;
    if (
      type?.maxHoldTime &&
      timer &&
      timer.enteredAt &&
      (!timer.exitedAt || isAfter(parseISO(timer.enteredAt), parseISO(timer.exitedAt)))
    ) {
      const time = parseFloat(type.maxHoldTime) * 60 - Math.abs(differenceInSeconds(new Date(), parseISO(timer.enteredAt)));
      setTimeOnTimer(time);
      setDelay(1000);
    } else {
      setTimeOnTimer(null);
      setDelay(null);
    }
  }, [type]);

  useEffect(() => {
    if (timeOnTimer === 0) {
      playWarning();
    }
  }, [timeOnTimer]);

  const tick = () => {
    const newTime = timeOnTimer - 1;
    setTimeOnTimer(newTime);
  };

  const calculateColor = (color1: string, color2: string, ratio: number): string => {
    //  calculate the color gradient in between to colors based on a ratio of 0-1

    const hex = x => {
      x = x.toString(16);
      return x.length === 1 ? '0' + x : x;
    };

    const r = Math.ceil(parseInt(color1.substring(1, 3), 16) * ratio + parseInt(color2.substring(1, 3), 16) * (1 - ratio));
    const g = Math.ceil(parseInt(color1.substring(3, 5), 16) * ratio + parseInt(color2.substring(3, 5), 16) * (1 - ratio));
    const b = Math.ceil(parseInt(color1.substring(5, 7), 16) * ratio + parseInt(color2.substring(5, 7), 16) * (1 - ratio));

    return `#${hex(r)}${hex(g)}${hex(b)}`;
  };

  const getColors = useCallback((): { background: string; timer: string } => {
    /**
     * This dynamically changes changes the color every second based on the enteredAt time, warning time and holding time
     * The states it goes through are:
     * grey/white: before warning time is reached
     * grey/white => yellow: after warning time is reached until half of the warning time has elapsed
     * yellow => red: after half of the warning time has elapsed
     * red: after the timer has reached 0
     **/
    const warningTime = warningTimerSetting && warningTimerSetting * 60;
    if ((timeOnTimer || timeOnTimer === 0) && warningTime >=0) {
      if (timeOnTimer > 0) {
        if (timeOnTimer < warningTime) {
          if (timeOnTimer > warningTime / 2) {
            return {
              timer: calculateColor(Color.GRAY, Color.WARNING, (timeOnTimer - warningTime / 2) / (warningTime / 2)),
              background:
                calculateColor(Color.TIMER, Color.WARNING, (timeOnTimer - warningTime / 2) / (warningTime / 2)) +
                TRANSPARENCY_HEX,
            };
          } else if (timeOnTimer <= warningTime / 2) {
            return {
              timer: calculateColor(Color.WARNING, Color.WARNING2, timeOnTimer / (warningTime / 2)),
              background: calculateColor(Color.WARNING, Color.WARNING2, timeOnTimer / (warningTime / 2)) + TRANSPARENCY_HEX,
            };
          }
        }
        return {
          timer: Color.GRAY,
          background: Color.TIMER + TRANSPARENCY_HEX,
        };
      }
      return {
        timer: Color.OVERTIME,
        background: Color.OVERTIME + TRANSPARENCY_HEX,
      };
    }
    return {
      timer: Color.GRAY,
      background: Color.TIMER + TRANSPARENCY_HEX,
    };
  }, [timeOnTimer]);

  useInterval(tick, delay);

  const getClass = () => {
    const width = cell?.current?.clientWidth;
    return classNames('content', {
      smallest: width < 50,
      smaller: width < 80,
      small: width < 110,
      regular: width >= 110,
    });
  };

  const colors = getColors();

  return (
    <div
      className={getClass()}
      ref={cell}
      style={{
        backgroundColor: colors.background,
      }}>
      <audio id='warning' src={ding} preload='auto' />

      <p>{getFoodTypeName(type)}</p>
      {(!!timeOnTimer || timeOnTimer === 0) && <WarningTimer time={timeOnTimer} color={colors.timer} blink={userInfo.blinkTimer} />}
    </div>
  );
};

export default Gastronom;
